import authClient from '@/api/authClient';
import authService from '@/api/authService';
import { Button, Group, List, Loader, Stack, Text, Title } from '@mantine/core';
import { createFileRoute, redirect } from '@tanstack/react-router';
import logo from 'common/assets/Mereko_Logo_Full.png';
import { useEffect, useState } from 'react';
import { useTranslator } from 'translations';
import z from 'zod';

const SearchSchema = z.object({
    client_id: z.string().optional(),
    scope: z.string().optional(),
});

/**
 * OAuth consent page for the MCP interface: the better-auth OAuth provider redirects here (with
 * the signed authorize query) when a client needs the user's consent. Accept/deny is posted back
 * to `/oauth2/consent` (the auth client attaches the signed query automatically), which returns
 * the url to send the user back to the requesting application.
 */
export const Route = createFileRoute('/consent')({
    component: RouteComponent,
    validateSearch: SearchSchema,
    async beforeLoad({ search }) {
        await authService.requireLogin(window.location.pathname + window.location.search);

        if (!search.client_id) {
            throw redirect({ to: '/' });
        }
    },
});

function RouteComponent() {
    const t = useTranslator();
    const { client_id, scope } = Route.useSearch();

    const [clientName, setClientName] = useState<string>();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(false);

    const scopes = (scope?.split(' ') ?? []).filter((s) => s !== 'openid');

    useEffect(() => {
        authClient
            .$fetch<{ client_name?: string; client_uri?: string }>('/oauth2/public-client', {
                query: { client_id },
            })
            .then(({ data }) => setClientName(data?.client_name ?? t('oauthConsent.unknownClient')))
            .catch(() => setClientName(t('oauthConsent.unknownClient')));
    }, [client_id, t]);

    async function decide(accept: boolean) {
        setSubmitting(true);
        setError(false);

        const { data, error: consentError } = await authClient.$fetch<{
            redirect: boolean;
            url: string;
        }>('/oauth2/consent', {
            method: 'POST',
            body: {
                accept,
                oauth_query: window.location.search,
            },
        });

        if (consentError || !data?.url) {
            setSubmitting(false);
            setError(true);
            return;
        }

        window.location.href = data.url;
    }

    return (
        <Stack
            align="center"
            justify="center"
            gap="md"
            css={{
                minHeight: '100dvh',
                padding: 'var(--mantine-spacing-md)',
            }}
        >
            <img
                css={{
                    width: '100%',
                    maxWidth: '10rem',
                    marginBottom: '2rem',
                }}
                src={logo}
                alt="Mereko Logo"
            />

            <Title order={2}>{t('oauthConsent.title')}</Title>

            {clientName === undefined ? (
                <Loader />
            ) : (
                <>
                    <Text ta="center" css={{ maxWidth: 480 }}>
                        {t('oauthConsent.description', { clientName })}
                    </Text>

                    {scopes.length > 0 && (
                        <Stack gap="xs" align="center">
                            <Text c="dimmed">{t('oauthConsent.scopesLabel')}</Text>
                            <List>
                                {scopes.map((s) => (
                                    <List.Item key={s}>
                                        {t.unknown(`oauthConsent.scopes.${s}`, undefined, {
                                            fallback: s,
                                        })}
                                    </List.Item>
                                ))}
                            </List>
                        </Stack>
                    )}

                    {error && <Text c="red">{t('oauthConsent.error')}</Text>}

                    <Group>
                        <Button
                            variant="default"
                            disabled={submitting}
                            onClick={() => decide(false)}
                        >
                            {t('oauthConsent.deny')}
                        </Button>
                        <Button loading={submitting} onClick={() => decide(true)}>
                            {t('oauthConsent.accept')}
                        </Button>
                    </Group>
                </>
            )}
        </Stack>
    );
}
